import { useMemo } from "react";

import { useTranslate } from "@similarweb/i18n-provider";
import { colorsPalettes } from "@similarweb/styles";
import { Flex, Text } from "@similarweb/ui-components/foundations";

import { LimitedChipItems } from "app/pages/sales-intelligence/common-components/filters-panel";
import { FilterResultContainer } from "app/pages/sales-intelligence/common-components/filters-panel/components/styles";
import type { IEmployeeLocationFilterDto } from "app/pages/sales-intelligence/domain/contacts/types/filter-values";
import { usePlaybookLocationOptions } from "app/pages/sales-intelligence/modules/playbooks/hooks/usePlaybookLocationOptions";
import { inclusionOptionMap } from "app/pages/sales-intelligence/types/commonMaps";
import { commonKeys } from "app/pages/sales-intelligence/utils/i18n/common";
import { buildLocationChips } from "app/pages/sales-intelligence/utils/locationChips";

type PlaybookEmployeeLocationSummaryProps = {
    employeeLocation?: IEmployeeLocationFilterDto;
};

export const PlaybookEmployeeLocationSummary = ({ employeeLocation }: PlaybookEmployeeLocationSummaryProps) => {
    const { translate } = useTranslate();
    const { countries, regions } = usePlaybookLocationOptions();

    const chips = useMemo(
        () => buildLocationChips({ value: employeeLocation, regions, countries }),
        [employeeLocation, regions, countries],
    );

    if (!chips.length) {
        return null;
    }

    const inclusion = employeeLocation?.inclusion ?? inclusionOptionMap.includeOnly;
    const inclusionLabel = translate(commonKeys.inclusion(inclusion));

    return (
        <Flex
            flexDirection="column"
            gap="8px"
            data-automation="playbook-summary-employee-location"
        >
            <Text
                fontVariant="input2"
                color={colorsPalettes.carbon["400"]}
            >
                {inclusionLabel}
            </Text>
            <FilterResultContainer>
                <LimitedChipItems
                    items={chips}
                    maxItems={chips.length}
                    hideActionButton
                    isTabExpanded
                />
            </FilterResultContainer>
        </Flex>
    );
};
